'use client';

import Link from 'next/link';
import PageLayout from '@/components/layout/PageLayout';
import { useState, useEffect, useRef, useCallback } from 'react';
import OtpInput from '@/components/ui/OtpInput';
import { requestLoginOtp, verifyOtp, googleLogin } from '@/lib/api/authApi';
import { useAuth } from '@/lib/auth/authContext';
import { useRouter } from 'next/navigation';
import { GoogleLogin, CredentialResponse, useGoogleOneTapLogin } from '@react-oauth/google';

export default function LoginPage() {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isMounted, setIsMounted] = useState(false);
  const [googleButtonWidth, setGoogleButtonWidth] = useState('320');
  
  // One Tap control state
  const [oneTapEnabled, setOneTapEnabled] = useState(false);
  const [oneTapAttempted, setOneTapAttempted] = useState(false);
  
  const { login, isAuthenticated, isLoading: authLoading } = useAuth();
  const router = useRouter();
  
  // Ref to track if we're currently processing a login
  const loginInProgressRef = useRef(false);

  // Ensure component is mounted on client side for Google OAuth
  useEffect(() => {
    setIsMounted(true);
    
    // Set Google button width based on screen size
    const updateButtonWidth = () => {
      if (window.innerWidth < 380) {
        setGoogleButtonWidth('280');
      } else {
        setGoogleButtonWidth('320');
      }
    };
    
    updateButtonWidth();
    window.addEventListener('resize', updateButtonWidth);
    
    return () => window.removeEventListener('resize', updateButtonWidth);
  }, []);

  // Redirect if already logged in - do this AFTER auth loading completes
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      router.push('/');
    }
  }, [isAuthenticated, authLoading, router]);

  // Enable One Tap ONLY after:
  // 1. Component is mounted
  // 2. Auth loading is complete
  // 3. User is NOT authenticated
  // 4. Not already in OTP flow
  // 5. Haven't already attempted One Tap
  // 6. No login is in progress
  useEffect(() => {
    // Wait a bit for everything to settle before enabling One Tap
    const timeoutId = setTimeout(() => {
      if (
        isMounted && 
        !authLoading && 
        !isAuthenticated && 
        !otpSent && 
        !oneTapAttempted &&
        !loading &&
        !loginInProgressRef.current
      ) {
        setOneTapEnabled(true);
      }
    }, 500); // 500ms delay to let auth state settle

    return () => clearTimeout(timeoutId);
  }, [isMounted, authLoading, isAuthenticated, otpSent, oneTapAttempted, loading]);

  // Phone number validation - just ensure there are 10 digits
  const isValidPhoneNumber = (number: string) => {
    return /^\d{10}$/.test(number);
  };
  
  // Handle phone number change
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Remove non-digit characters
    let value = e.target.value.replace(/\D/g, '');
    
    // If longer than 10 digits, take the last 10
    if (value.length > 10) {
      value = value.slice(-10);
    }
    
    setPhoneNumber(value);
  };

  // Request OTP
  const handleRequestOtp = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (!isValidPhoneNumber(phoneNumber)) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await requestLoginOtp(phoneNumber);
      if (response.status === 'success') {
        setOtpSent(true);
      } else {
        setError('Failed to send OTP. Please try again.');
      }
    } catch (err) {
      console.error('Error requesting OTP:', err);
      setError('An error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async (otpCode: string) => {
    setLoading(true);
    setError('');
    
    try {
      const response = await verifyOtp(phoneNumber, otpCode);
      if (response.status === 'success') {
        login(response.data);
        router.push('/');
      } else {
        setError('Invalid OTP. Please try again.');
      }
    } catch (err) {
      console.error('Error verifying OTP:', err);
      setError('Failed to verify OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle Google login success - memoized to prevent unnecessary re-renders
  const handleGoogleSuccess = useCallback(async (credentialResponse: CredentialResponse) => {
    // Prevent duplicate login attempts
    if (loginInProgressRef.current) {
      return;
    }
    
    if (!credentialResponse.credential) {
      setError('Failed to receive Google credentials. Please try again.');
      return;
    }

    loginInProgressRef.current = true;
    setOneTapEnabled(false); // Disable One Tap immediately
    setLoading(true);
    setError('');

    try {
      const response = await googleLogin(credentialResponse.credential);
      if (response.status === 'success') {
        login(response.data);
        router.push('/');
      } else {
        setError('Google login failed. Please try again.');
        loginInProgressRef.current = false;
      }
    } catch (err) {
      console.error('Error with Google login:', err);
      setError('Google login failed. Please try again or use phone number.');
      loginInProgressRef.current = false;
    } finally {
      setLoading(false);
    }
  }, [login, router]);

  // Handle Google login error - memoized
  const handleGoogleError = useCallback(() => {
    setError('Google login failed. Please try again or use phone number.');
  }, []);

  // Handle One Tap specific error/close - mark as attempted so we don't retry
  const handleOneTapError = useCallback(() => {
    // Silently mark as attempted - don't show error for One Tap dismissal
    setOneTapAttempted(true);
    setOneTapEnabled(false);
  }, []);

  // Format phone number for display
  const formatPhoneForDisplay = (phone: string) => {
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
  };

  // Determine if we should show One Tap
  const shouldShowOneTap = oneTapEnabled && !oneTapAttempted && !loading && !otpSent;

  return (
    <PageLayout>
      {/* One Tap Login - Only render when conditions are met */}
      {shouldShowOneTap && (
        <GoogleOneTapWrapper 
          onSuccess={handleGoogleSuccess} 
          onError={handleOneTapError}
        />
      )}

      <div className="flex items-center justify-center px-4 py-10 sm:px-6 md:py-16 lg:px-8">
        <div className="w-full max-w-md">
          <div className="rounded-2xl border border-zinc-200 bg-white px-4 py-6 shadow-sm sm:px-10 sm:py-8 dark:border-zinc-800 dark:bg-zinc-900">
            <div className="text-center mb-6 sm:mb-8">
              {/* Logo/Icon */}
              <div className="flex justify-center mb-3 sm:mb-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
              
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-zinc-900 dark:text-white">
                {otpSent ? 'Verify Your Identity' : 'Welcome Back'}
              </h1>
              <p className="mt-1 text-sm md:text-base text-zinc-500 dark:text-zinc-400 leading-relaxed">
                {otpSent 
                  ? (
                    <>
                      We&apos;ve sent a verification code to<br />
                      <span className="font-semibold text-zinc-900 dark:text-white">{formatPhoneForDisplay(phoneNumber)}</span>
                    </>
                  )
                  : 'Access exclusive market insights and powerful trading tools'}
              </p>
            </div>
            
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
                <p className="text-xs sm:text-sm text-red-700 dark:text-red-300 break-words">{error}</p>
              </div>
            )}
            
            {!otpSent ? (
              <div className="space-y-6">
                {/* Google Login - Only render on client side */}
                {isMounted && (
                  <div className="space-y-4">
                    <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 sm:p-8 dark:border-zinc-800 dark:bg-zinc-800/60">
                      <div className="flex flex-col items-center gap-3 sm:gap-4">
                        <div className="text-center space-y-1 sm:space-y-2">
                          <h3 className="text-lg sm:text-xl font-bold text-zinc-900 dark:text-white">
                            Sign in with Google
                          </h3>
                          <p className="text-xs sm:text-sm text-zinc-500 dark:text-zinc-400">
                            Quick & secure access to your account
                          </p>
                        </div>
                        
                        <div className="w-full flex justify-center pt-1 sm:pt-2">
                          <div className="max-w-full overflow-hidden">
                            {loading ? (
                              <div className="flex items-center justify-center gap-2 py-3 px-6 bg-emerald-600 text-white rounded-lg">
                                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span className="font-medium">Signing in...</span>
                              </div>
                            ) : (
                              <GoogleLogin
                                onSuccess={handleGoogleSuccess}
                                onError={handleGoogleError}
                                theme="filled_blue"
                                size="large"
                                text="signin_with"
                                shape="rectangular"
                                width={googleButtonWidth}
                                logo_alignment="left"
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* OR Divider - Enhanced */}
                {/* PHONE LOGIN TEMPORARILY HIDDEN - Code kept for future use */}
                {false && (
                  <div className="relative py-2">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-zinc-200 dark:border-zinc-800"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white dark:bg-zinc-900 text-zinc-500 dark:text-zinc-400 font-semibold tracking-wider">
                        OR USE PHONE NUMBER
                      </span>
                    </div>
                  </div>
                )}

                {/* Phone number input form - Enhanced styling */}
                {/* PHONE LOGIN TEMPORARILY HIDDEN - Code kept for future use */}
                {false && (
                <form className="space-y-5" onSubmit={handleRequestOtp}>
                  <div className="space-y-2">
                    <label htmlFor="phoneNumber" className="block text-sm font-semibold text-zinc-700 dark:text-zinc-300 flex items-center gap-2">
                      <svg className="w-5 h-5 text-zinc-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                      </svg>
                      Phone Number
                    </label>
                    <div className="relative group">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                        <span className="text-zinc-600 dark:text-zinc-400 font-semibold text-sm">+91</span>
                      </div>
                      <input
                        id="phoneNumber"
                        name="phoneNumber"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel"
                        required
                        value={phoneNumber}
                        onChange={handlePhoneChange}
                        className="appearance-none block w-full pl-16 pr-4 py-3 rounded-lg border border-zinc-300 bg-white text-zinc-900 placeholder:text-zinc-400 dark:border-zinc-700 dark:bg-zinc-900 dark:text-white focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 text-base transition-colors"
                        placeholder="10-digit mobile number"
                      />
                      {phoneNumber && isValidPhoneNumber(phoneNumber) && (
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                    {phoneNumber && !isValidPhoneNumber(phoneNumber) && (
                      <p className="mt-1.5 text-xs text-red-600 dark:text-red-400 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        Please enter a valid 10-digit mobile number
                      </p>
                    )}
                  </div>

                  <div>
                    <button
                      type="submit"
                      disabled={loading || !isValidPhoneNumber(phoneNumber)}
                      className="w-full flex justify-center items-center gap-2 py-3 px-4 rounded-lg text-base font-semibold text-white bg-emerald-600 hover:bg-emerald-700 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/40 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {loading ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Sending OTP...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          <span>Get OTP</span>
                        </>
                      )}
                    </button>
                  </div>
                  
                  <div className="text-center pt-2">
                    <p className="text-xs text-zinc-500 dark:text-zinc-400">
                      Need help? <Link href="/contact" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium">Contact our support team</Link>
                    </p>
                  </div>
                </form>
                )}
              </div>
            ) : (
              // OTP verification form
              <div className="space-y-4 sm:space-y-6">
                {/* Success indicator */}
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 sm:p-4 dark:border-emerald-900/50 dark:bg-emerald-500/10">
                  <div className="flex items-start sm:items-center gap-2 sm:gap-3">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-500 flex-shrink-0 mt-0.5 sm:mt-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="text-xs sm:text-sm font-semibold text-emerald-800 dark:text-emerald-300">
                        OTP sent successfully!
                      </p>
                      <p className="text-xs text-emerald-700 dark:text-emerald-400 mt-0.5">
                        Check your phone for the verification code
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm sm:text-base font-semibold text-zinc-700 dark:text-zinc-300 text-center mb-3 sm:mb-4 flex items-center justify-center gap-2">
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    <span className="text-sm sm:text-base">Enter 6-digit verification code</span>
                  </label>
                  <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-4 sm:p-6 dark:border-zinc-800 dark:bg-zinc-800/60">
                    <OtpInput 
                      length={6} 
                      onComplete={handleVerifyOtp} 
                      isSubmitting={loading}
                    />
                    {loading && (
                      <div className="mt-3 sm:mt-4 flex items-center justify-center gap-2 text-xs sm:text-sm text-emerald-600 dark:text-emerald-400">
                        <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="font-medium">Verifying...</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0 pt-2">
                  <button
                    type="button"
                    onClick={() => setOtpSent(false)}
                    className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-zinc-200 transition-colors"
                  >
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Change number
                  </button>
                  
                  <button
                    type="button" 
                    onClick={() => handleRequestOtp()}
                    disabled={loading}
                    className={`inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium transition-colors ${
                      loading 
                        ? 'text-zinc-400 cursor-not-allowed' 
                        : 'text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300'
                    }`}
                  >
                    <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {loading ? 'Please wait...' : 'Resend OTP'}
                  </button>
                </div>
                
                <div className="text-center pt-2">
                  <p className="text-xs text-zinc-500 dark:text-zinc-400">
                    Need help? <Link href="/contact" className="text-emerald-600 dark:text-emerald-400 hover:underline font-medium">Contact our support team</Link>
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PageLayout>
  );
}

// Separate wrapper component for One Tap Login
// This component is only rendered when conditions are met
// It uses a ref to ensure the hook only runs once
function GoogleOneTapWrapper({ 
  onSuccess, 
  onError 
}: { 
  onSuccess: (response: CredentialResponse) => void;
  onError: () => void;
}) {
  const hasAttemptedRef = useRef(false);
  
  // Wrap handlers to mark as attempted after first call
  const wrappedOnSuccess = useCallback((response: CredentialResponse) => {
    if (hasAttemptedRef.current) return;
    hasAttemptedRef.current = true;
    onSuccess(response);
  }, [onSuccess]);
  
  const wrappedOnError = useCallback(() => {
    if (hasAttemptedRef.current) return;
    hasAttemptedRef.current = true;
    onError();
  }, [onError]);

  useGoogleOneTapLogin({
    onSuccess: wrappedOnSuccess,
    onError: wrappedOnError,
    auto_select: false, // Don't auto-select - let user choose account
    cancel_on_tap_outside: true, // Allow dismissal
    use_fedcm_for_prompt: false, // Disable FedCM to avoid CORS issues
  });
  
  return null;
}
